/**
 * Thermal printer command sequences (ESC/POS): init, set density, raster data, finalize.
 * ESC/POS-like or proprietary; adjust init/raster/finalize if device uses different protocol.
 */

/** Initialize / reset printer */
export function buildInit(): Uint8Array {
  // ESC @ = Initialize printer (common ESC/POS)
  const init = new Uint8Array([0x1b, 0x40]);
  return init;
}

/** Set print density/darkness (0–100). ESC ( E n m l — density; adapt to device if needed. */
export function buildSetDensity(density: number): Uint8Array {
  // Clamp 0–100; map to printer byte if needed (e.g. 0–255)
  const n = Math.max(0, Math.min(100, Math.round(density)));
  // Simple density: GS ( K pL pH cn m (e.g. m = 0..8). Use a simple variant; device may use different cmd.
  const m = Math.floor((n / 100) * 8);
  return new Uint8Array([0x1d, 0x28, 0x4b, 0x02, 0x00, 0x31, m]);
}

/** Build raster print command (bit image). Row-based raster; width in bytes. ESC * m nL nH. */
export function buildRasterRow(rowBytes: Uint8Array): Uint8Array {
  const n = rowBytes.length;
  const cmd = new Uint8Array(5 + n);
  cmd[0] = 0x1b;
  cmd[1] = 0x2a;
  cmd[2] = 33; // 24-dot double-density
  cmd[3] = n & 0xff;
  cmd[4] = (n >> 8) & 0xff;
  cmd.set(rowBytes, 5);
  return cmd;
}

/** GS v 0 raster (1D 76 30 m xL xH yL yH d1...dk). Optional alternative; default remains ESC * row-by-row. */
export function buildRasterGsV0(raster: Uint8Array, widthBytes: number, height: number, m = 0): Uint8Array {
  const x = widthBytes;
  const y = height;
  const cmd = new Uint8Array(8 + raster.length);
  cmd[0] = 0x1d;
  cmd[1] = 0x76;
  cmd[2] = 0x30;
  cmd[3] = m;
  cmd[4] = x & 0xff;
  cmd[5] = (x >> 8) & 0xff;
  cmd[6] = y & 0xff;
  cmd[7] = (y >> 8) & 0xff;
  cmd.set(raster, 8);
  return cmd;
}

/** Line feed + form feed. Modular so finalize can be adjusted per device. */
export function buildFinalize(): Uint8Array {
  return new Uint8Array([0x0a, 0x0c]); // LF, FF
}

/** Build full raster payload: init + density + one buildRasterRow per row + finalize. */
export function buildRasterPayload(
  raster: Uint8Array,
  widthBytes: number,
  height: number,
  density?: number
): Uint8Array {
  const parts: Uint8Array[] = [];
  parts.push(buildInit());
  if (density != null && density > 0) {
    parts.push(buildSetDensity(density));
  }
  for (let y = 0; y < height; y++) {
    const row = raster.slice(y * widthBytes, (y + 1) * widthBytes);
    parts.push(buildRasterRow(row));
  }
  parts.push(buildFinalize());
  const total = parts.reduce((acc, p) => acc + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

/** Build minimal test payload: init + form feed */
export function buildTestPayload(density?: number): Uint8Array {
  const parts: Uint8Array[] = [];
  parts.push(buildInit());
  if (density != null && density > 0) {
    parts.push(buildSetDensity(density));
  }
  parts.push(buildFinalize());
  const total = parts.reduce((acc, p) => acc + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}
